import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Section } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import MemberDrillDown from "@/components/MemberDrillDown";
import MyDay from "@/components/MyDay";
import {
  Plus, ArrowUpRight, Clock, CheckCircle2, RefreshCw, Gauge,
  TrendingUp, TrendingDown, Zap, Rocket, Dumbbell,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";

const PIE_COLORS = [
  "#4F46E5", "#EC4899", "#14B8A6", "#F59E0B",
  "#8B5CF6", "#EF4444", "#06B6D4", "#84CC16",
];

const AVATAR_COLORS = [
  "bg-pink-500", "bg-violet-500", "bg-emerald-500", "bg-orange-500",
  "bg-rose-500", "bg-sky-500", "bg-amber-500", "bg-indigo-500",
];

function minutesToHuman(m) {
  if (!m || m <= 0) return "0h 0m";
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${mm}m`;
}

function Delta({ pct, inverse = false }) {
  if (pct === 0 || pct == null)
    return <span className="text-xs text-neutral-400">—</span>;
  const isUp = pct > 0;
  // For "revisions", up = bad (inverse true)
  const isGood = inverse ? !isUp : isUp;
  const Icon = isUp ? TrendingUp : TrendingDown;
  const color = isGood ? "text-emerald-600" : "text-rose-600";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`}>
      <Icon size={12} />
      {Math.abs(pct)}% from last month
    </span>
  );
}

function KpiCard({ label, value, hint, Icon, accent = "indigo", delta, inverse, testid }) {
  // Each KPI gets a coloured gradient panel + matching icon chip + tinted accent stripe.
  const themes = {
    indigo: {
      grad: "from-indigo-50 via-white to-white",
      stripe: "bg-indigo-500",
      iconBg: "bg-indigo-500",
      iconColor: "text-white",
      labelColor: "text-indigo-700",
    },
    emerald: {
      grad: "from-emerald-50 via-white to-white",
      stripe: "bg-emerald-500",
      iconBg: "bg-emerald-500",
      iconColor: "text-white",
      labelColor: "text-emerald-700",
    },
    rose: {
      grad: "from-rose-50 via-white to-white",
      stripe: "bg-rose-500",
      iconBg: "bg-rose-500",
      iconColor: "text-white",
      labelColor: "text-rose-700",
    },
    violet: {
      grad: "from-violet-50 via-white to-white",
      stripe: "bg-violet-500",
      iconBg: "bg-violet-500",
      iconColor: "text-white",
      labelColor: "text-violet-700",
    },
  };
  const t = themes[accent] || themes.indigo;
  return (
    <div
      className={`relative border border-neutral-200 bg-gradient-to-br ${t.grad} p-6 flex flex-col gap-3 overflow-hidden transition-shadow hover:shadow-md`}
      data-testid={testid}
    >
      <div className={`absolute top-0 left-0 w-1 h-full ${t.stripe}`} />
      <div className="flex items-start justify-between">
        <div className={`text-[10px] uppercase tracking-[0.2em] ${t.labelColor} font-semibold`}>{label}</div>
        <div className={`w-9 h-9 ${t.iconBg} rounded-sm grid place-items-center shadow-sm`}>
          <Icon size={16} className={t.iconColor} />
        </div>
      </div>
      <div className="font-display text-4xl font-bold tracking-tight text-neutral-900">{value}</div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-neutral-500">{hint}</span>
        <Delta pct={delta} inverse={inverse} />
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    Excellent: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Good: "bg-sky-100 text-sky-800 border-sky-200",
    "Needs review": "bg-amber-100 text-amber-800 border-amber-200",
    Idle: "bg-neutral-100 text-neutral-600 border-neutral-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-1 text-[10px] uppercase tracking-widest font-semibold border rounded-sm ${map[status] || map.Idle}`}>
      {status}
    </span>
  );
}

function BadgeRow({ badges }) {
  if (!badges || badges.length === 0) return <span className="text-neutral-400 text-sm">—</span>;
  const map = {
    speed: { Icon: Zap, color: "text-amber-500", title: "Speed — 150%+ efficiency" },
    powerhouse: { Icon: Dumbbell, color: "text-violet-600", title: "Powerhouse — 5+ tasks done" },
    rocket: { Icon: Rocket, color: "text-rose-500", title: "Rocket — 10h+ logged" },
    runner: { Icon: Rocket, color: "text-sky-500", title: "Runner — 3+ tasks done" },
  };
  return (
    <div className="flex items-center gap-1.5">
      {badges.map((b) => {
        const cfg = map[b];
        if (!cfg) return null;
        const { Icon, color, title } = cfg;
        return <Icon key={b} size={16} className={color} aria-label={title} />;
      })}
    </div>
  );
}

function Avatar({ name, idx }) {
  const initials = (name || "?")
    .split(" ")
    .map(p => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div className={`w-9 h-9 ${AVATAR_COLORS[idx % AVATAR_COLORS.length]} text-white rounded-full grid place-items-center text-xs font-semibold shrink-0`}>
      {initials}
    </div>
  );
}

function Sparkline({ trend }) {
  if (!trend || trend.length === 0) {
    return <span className="text-neutral-300 text-xs">—</span>;
  }
  const data = trend.map((w, i) => ({ i, hours: Math.round((w.hours_minutes || 0) / 6) / 10 }));
  const total = data.reduce((s, d) => s + d.hours, 0);
  if (total === 0) {
    return (
      <div className="flex items-center justify-center" title="No activity in the last 4 weeks">
        <div className="h-[1px] w-14 bg-neutral-200" />
      </div>
    );
  }
  const last = data[data.length - 1].hours;
  const prev = data[data.length - 2]?.hours ?? last;
  const up = last >= prev;
  const stroke = up ? "#10B981" : "#F43F5E";
  const last4 = trend.map(w => `${w.week_start}: ${Math.round((w.hours_minutes || 0) / 6) / 10}h`).join(" · ");
  return (
    <div className="w-20 h-8" title={`Weekly hours — ${last4}`}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 3, right: 2, left: 2, bottom: 3 }}>
          <Line
            type="monotone"
            dataKey="hours"
            stroke={stroke}
            strokeWidth={1.8}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const [stats, setStats] = useState(null);
  const [visual, setVisual] = useState(null);
  const [productivity, setProductivity] = useState([]);
  const [projects, setProjects] = useState([]);
  const [drillUserId, setDrillUserId] = useState(null);
  const [drillIdx, setDrillIdx] = useState(0);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats").then(r => setStats(r.data)).catch(() => {}),
      api.get("/dashboard/visual").then(r => setVisual(r.data)).catch(() => {}),
      api.get("/team-productivity").then(r => setProductivity(r.data)).catch(() => {}),
      api.get("/projects").then(r => setProjects(r.data.slice(0, 5))).catch(() => {}),
    ]);
  }, []);

  const kpis = visual?.kpis;
  const benchmarkData = visual?.benchmark_vs_actual || [];
  const typeDist = visual?.task_type_distribution || [];

  const totalHours = useMemo(() => minutesToHuman(kpis?.total_hours_minutes || 0), [kpis]);
  const avgPerTask = kpis?.avg_minutes_per_task ? `Avg ${kpis.avg_minutes_per_task}min/task` : "No tasks yet";

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Overview"
        title="Dashboard"
        description={
          visual
            ? `${visual.active_members} team member${visual.active_members === 1 ? "" : "s"} active today · ${isAdmin ? "studio-wide view" : "your personal productivity"}`
            : "Live read-out of productivity, benchmarks and the team leaderboard."
        }
        testid="dashboard-header"
        actions={
          <>
            {isAdmin && (
              <>
                <Link to="/clients"><Button variant="outline" className="rounded-sm" data-testid="dashboard-add-client-btn"><Plus size={14} className="mr-1" /> Add client</Button></Link>
                <Link to="/projects"><Button className="rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid="dashboard-new-project-btn"><Plus size={14} className="mr-1" /> New task</Button></Link>
              </>
            )}
          </>
        }
      />

      {/* My Day widget — assignee-scoped work list */}
      <Section>
        <MyDay />
      </Section>

      {/* KPI Row */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
          <KpiCard
            label="Total hours"
            value={totalHours}
            hint="This month"
            Icon={Clock}
            accent="indigo"
            delta={kpis?.total_hours_delta_pct}
            testid="kpi-total-hours"
          />
          <KpiCard
            label="Tasks completed"
            value={kpis?.tasks_completed_month ?? 0}
            hint={`${kpis?.tasks_total ?? 0} total`}
            Icon={CheckCircle2}
            accent="emerald"
            delta={kpis?.tasks_completed_delta_pct}
            testid="kpi-tasks-completed"
          />
          <KpiCard
            label="Total revisions"
            value={kpis?.total_revisions ?? 0}
            hint={avgPerTask}
            Icon={RefreshCw}
            accent="rose"
            delta={kpis?.revisions_delta_pct}
            inverse
            testid="kpi-revisions"
          />
          <KpiCard
            label="Efficiency score"
            value={`${kpis?.efficiency_pct ?? 0}%`}
            hint="Benchmark vs Actual"
            Icon={Gauge}
            accent="violet"
            delta={kpis?.efficiency_delta_pct}
            testid="kpi-efficiency"
          />
        </div>
      </Section>

      {/* Charts Row */}
      <Section className="grid grid-cols-1 lg:grid-cols-5 gap-4 pt-2">
        <div className="lg:col-span-3 border border-neutral-200 bg-white p-6 relative overflow-hidden" data-testid="benchmark-chart-card">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500" />
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-violet-700 font-semibold mb-1">Performance</div>
              <div className="font-display text-lg font-bold">Benchmark vs Actual time (avg min/task)</div>
            </div>
          </div>
          {benchmarkData.length === 0 ? (
            <div className="h-[280px] grid place-items-center text-sm text-neutral-500">
              Link tasks to templates to compare benchmark vs actual time.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={benchmarkData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
                <XAxis dataKey="category" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={55} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="benchmark_min" name="Benchmark" fill="#A5B4FC" radius={[4, 4, 0, 0]} />
                <Bar dataKey="actual_min" name="Actual" fill="#4F46E5" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="lg:col-span-2 border border-neutral-200 bg-white p-6 relative overflow-hidden" data-testid="distribution-chart-card">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-500 via-rose-500 to-orange-500" />
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-rose-700 font-semibold mb-1">Mix</div>
            <div className="font-display text-lg font-bold">Task type distribution</div>
          </div>
          {typeDist.length === 0 ? (
            <div className="h-[280px] grid place-items-center text-sm text-neutral-500 text-center px-6">
              Distribution will appear once tasks are created from templates.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={typeDist}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={95}
                  paddingAngle={2}
                  label={({ name, pct }) => `${name} ${pct}%`}
                  labelLine={{ stroke: "#999", strokeWidth: 0.5 }}
                  style={{ fontSize: 11 }}
                >
                  {typeDist.map((d, i) => (
                    <Cell key={d.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </Section>

      {/* Team productivity (full width — Leaderboard moved to its own page) */}
      <Section className="pt-2">
        <div className="border border-neutral-200 bg-white" data-testid="team-productivity-card">
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-1">
                {isAdmin ? "Team performance" : "Your performance"}
              </div>
              <div className="font-display text-xl font-bold">
                {isAdmin ? "Team productivity" : "Your productivity"}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-widest text-neutral-400 hidden sm:block">Click any row for the 8-week drill-down</span>
              <span className="text-[10px] uppercase tracking-widest text-neutral-500">This month</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="productivity-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-100">
                  <th className="text-left font-semibold px-6 py-3">Member</th>
                  <th className="text-right font-semibold px-3 py-3">Hours logged</th>
                  <th className="text-right font-semibold px-3 py-3">Tasks done</th>
                  <th className="text-right font-semibold px-3 py-3">Revisions</th>
                  <th className="text-right font-semibold px-3 py-3">Efficiency</th>
                  <th className="text-center font-semibold px-3 py-3">Trend (4w)</th>
                  <th className="text-center font-semibold px-3 py-3">Badges</th>
                  <th className="text-center font-semibold px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {productivity.length === 0 ? (
                  <tr><td colSpan={8} className="p-10 text-center text-neutral-500 text-sm">No activity yet.</td></tr>
                ) : productivity.map((r, i) => (
                  <tr
                    key={r.user_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => { setDrillUserId(r.user_id); setDrillIdx(i); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDrillUserId(r.user_id); setDrillIdx(i); } }}
                    className="border-b border-neutral-50 hover:bg-neutral-50 transition-colors cursor-pointer focus:bg-neutral-100 focus:outline-none"
                    data-testid={`productivity-row-${r.user_id}`}
                  >
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={r.name} idx={i} />
                        <div className="min-w-0">
                          <div className="font-medium truncate">{r.name}</div>
                          <div className="text-[11px] text-neutral-500 truncate">{r.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">{minutesToHuman(r.hours_minutes)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{r.tasks_done}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-neutral-600">
                      {r.revisions} <span className="text-neutral-400 text-xs">({r.revisions_pct}%)</span>
                    </td>
                    <td className="px-3 py-3 text-right font-display font-bold tabular-nums">{r.efficiency_pct}%</td>
                    <td className="px-3 py-3" data-testid={`productivity-sparkline-${r.user_id}`}>
                      <div className="flex items-center justify-center"><Sparkline trend={r.trend} /></div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-center"><BadgeRow badges={r.badges} /></div>
                    </td>
                    <td className="px-6 py-3 text-center"><StatusPill status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!isAdmin && (
            <div className="px-6 py-3 border-t border-neutral-100 text-[11px] text-neutral-500">
              You&apos;re seeing your personal productivity. Admins see the full team.
            </div>
          )}
        </div>
      </Section>

      <MemberDrillDown
        userId={drillUserId}
        avatarIdx={drillIdx}
        open={!!drillUserId}
        onClose={() => setDrillUserId(null)}
      />

      {/* Recent projects */}
      <Section className="pt-2">
        <div className="border border-neutral-200 bg-white" data-testid="recent-projects-card">
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-1">Pipeline</div>
              <div className="font-display text-xl font-bold">Recent tasks</div>
            </div>
            <Link to="/projects" className="text-xs text-neutral-500 hover:text-neutral-900" data-testid="view-all-projects-link">View all →</Link>
          </div>
          <div className="divide-y divide-neutral-100">
            {projects.length === 0 ? (
              <div className="p-8 text-xs text-neutral-500 text-center">
                No tasks yet — create one to get started.
              </div>
            ) : projects.map(p => (
              <Link key={p.id} to={`/projects/${p.id}`} className="px-6 py-4 hover:bg-neutral-50 transition-colors flex items-center justify-between" data-testid={`recent-proj-${p.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-1.5 h-8" style={{ background: p.color || "#0A0A0A" }} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">{(p.status || "").replace("_", " ")}</div>
                  </div>
                </div>
                <ArrowUpRight size={14} className="text-neutral-400" />
              </Link>
            ))}
          </div>
        </div>
      </Section>

      {/* Micro stats footer - open tasks overview */}
      {stats && (
        <Section className="pt-2 pb-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="footer-stats">
            <div className="border border-neutral-200 p-4 bg-gradient-to-br from-sky-50 to-white relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-sky-500" />
              <div className="text-[10px] uppercase tracking-widest text-sky-700 font-semibold mb-1">Active projects</div>
              <div className="font-display text-2xl font-bold">{stats.active_projects}</div>
            </div>
            <div className="border border-neutral-200 p-4 bg-gradient-to-br from-amber-50 to-white relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-amber-500" />
              <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold mb-1">Open tasks</div>
              <div className="font-display text-2xl font-bold">{stats.open_tasks} <span className="text-xs text-rose-600 font-medium">· {stats.overdue_tasks} overdue</span></div>
            </div>
            <div className="border border-neutral-200 p-4 bg-gradient-to-br from-fuchsia-50 to-white relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-fuchsia-500" />
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-700 font-semibold mb-1">Clients</div>
              <div className="font-display text-2xl font-bold">{stats.total_clients}</div>
            </div>
            <div className="border border-neutral-200 p-4 bg-gradient-to-br from-teal-50 to-white relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-teal-500" />
              <div className="text-[10px] uppercase tracking-widest text-teal-700 font-semibold mb-1">Team</div>
              <div className="font-display text-2xl font-bold">{stats.total_team}</div>
            </div>
          </div>
        </Section>
      )}
    </div>
  );
}
